import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, CircularProgress, Divider, Table, 
  TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Button, Toolbar, Alert, Chip } from "@mui/material";
import { listIndexes, deleteIndex } from "../api/agentApi";
import { Link } from "react-router-dom";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";

function IndexStatusTable({ indexType, title }) {
  const [indexes, setIndexes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(true);
  const [error, setError] = useState("");

  // Initial column widths (px) - fit Box width (900px)
  const [colWidths, setColWidths] = useState({
    id: 120,
    status: 120,
    name: 220,
    description: 340 // No resizer for description
  });

  const handleResize = (col, data) => {
    setColWidths(w => ({ ...w, [col]: data.size.width }));
  };

  useEffect(() => {
    setLoading(true);
    listIndexes(indexType)
      .then(data => {
        setIndexes(data.indexes || []);
        setLoading(false);
      })
      .catch(err => {
        setError("Failed to fetch collections");
        setLoading(false);
      });
  }, [indexType]);

  const handleDelete = async (indexId, indexName) => {
    if (!window.confirm(`Delete knowledge base '${indexName}'(id: '${indexId}')? This cannot be undone.`)) return;
    setDeleting(indexId);
    setError("");
    try {
      await deleteIndex(indexId);
      setIndexes(indexes.filter(idx => idx.index_id !== indexId));
    } catch (e) {
      let detail = "";
      if (e?.message) {
        try {
          const parsed = JSON.parse(e.message);
          if (parsed && typeof parsed.detail === "string") {
            detail = parsed.detail;
          } else {
            detail = e.message;
          }
        } catch {
          detail = e.message;
        }
      } else if (typeof e?.detail === "string") {
        detail = e.detail;
      } else {
        detail = "Failed to delete knowledge base.";
      }
      setError(detail);
    }
    setDeleting("");
  };

  return (
    <Box>
      {/* <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 0 }}>
        <Typography variant="h5" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</Typography>
      </Toolbar> */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        {loading ? <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box> : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                {/* Name column with resizer */}
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem", p: 0, position: "relative", whiteSpace: "nowrap" }} width={colWidths.name}>
                  <Box sx={{ display: "flex", alignItems: "center", height: 1 }}>
                    <Box sx={{ px: 1, py: 1, flex: 1 }}>Name</Box>
                    <ResizableBox
                      width={colWidths.name}
                      height={40}
                      axis="x"
                      minConstraints={[100, 40]}
                      maxConstraints={[400, 40]}
                      onResize={(e, data) => handleResize("name", data)}
                      handle={<span style={{ cursor: "col-resize", padding: "0 8px", position: "absolute", right: -10, top: 0, height: "100%", display: "flex", alignItems: "center", color: "#1976d2", fontWeight: 900, fontSize: "1.5rem", borderRadius: "4px" }}>⋮</span>}
                      resizeHandles={["e"]}
                      style={{ display: "inline-block", verticalAlign: "middle", background: "transparent", boxShadow: "none", border: "none", position: "relative" }}
                    >
                      <span style={{ display: "none" }} />
                    </ResizableBox>
                  </Box>
                </TableCell>
                {/* Description column */}
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem", p: 0, whiteSpace: "normal" }} width={colWidths.description}>
                  <Box sx={{ display: "flex", alignItems: "center", height: 1 }}>
                    <Box sx={{ px: 1, py: 1, flex: 1 }}>Description</Box>
                    <ResizableBox
                      width={colWidths.id}
                      height={40}
                      axis="x"
                      minConstraints={[60, 40]}
                      maxConstraints={[400, 40]}
                      onResize={(e, data) => handleResize("description", data)}
                      handle={<span style={{ cursor: "col-resize", padding: "0 8px", position: "absolute", right: -10, top: 0, height: "100%", display: "flex", alignItems: "center", color: "#1976d2", fontWeight: 900, fontSize: "1.5rem", borderRadius: "4px" }}>⋮</span>}
                      resizeHandles={["e"]}
                      style={{ display: "inline-block", verticalAlign: "middle", background: "transparent", boxShadow: "none", border: "none", position: "relative" }}
                    >
                      <span style={{ display: "none" }} />
                    </ResizableBox>
                  </Box>
                </TableCell>
                {/* Status column with resizer */}
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem", p: 0, position: "relative", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} width={colWidths.status}>
                  <Box sx={{ display: "flex", alignItems: "center", height: 1 }}>
                    <Box sx={{ px: 1, py: 1, flex: 1 }}>Status</Box>
                    <ResizableBox
                      width={colWidths.status}
                      height={40}
                      axis="x"
                      minConstraints={[60, 40]}
                      maxConstraints={[140, 40]}
                      onResize={(e, data) => handleResize("status", data)}
                      handle={<span style={{ cursor: "col-resize", padding: "0 8px", position: "absolute", right: -10, top: 0, height: "100%", display: "flex", alignItems: "center", color: "#1976d2", fontWeight: 900, fontSize: "1.5rem", borderRadius: "4px" }}>⋮</span>}
                      resizeHandles={["e"]}
                      style={{ display: "inline-block", verticalAlign: "middle", background: "transparent", boxShadow: "none", border: "none", position: "relative" }}
                    >
                      <span style={{ display: "none" }} />
                    </ResizableBox>
                  </Box>
                </TableCell>
                {/* Index ID column with resizer */}
                {/* <TableCell sx={{ fontWeight: 700, fontSize: "1rem", p: 0, position: "relative", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} width={colWidths.id}>
                  <Box sx={{ display: "flex", alignItems: "center", height: 1 }}>
                    <Box sx={{ px: 1, py: 1, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Index ID</Box>
                    <ResizableBox
                      width={colWidths.id}
                      height={40}
                      axis="x"
                      minConstraints={[60, 40]}
                      maxConstraints={[200, 40]}
                      onResize={(e, data) => handleResize("id", data)}
                      handle={<span style={{ cursor: "col-resize", padding: "0 8px", position: "absolute", right: -10, top: 0, height: "100%", display: "flex", alignItems: "center", color: "#1976d2", fontWeight: 900, fontSize: "1.5rem", borderRadius: "4px" }}>⋮</span>}
                      resizeHandles={["e"]}
                      style={{ display: "inline-block", verticalAlign: "middle", background: "transparent", boxShadow: "none", border: "none", position: "relative" }}
                    >
                      <span style={{ display: "none" }} />
                    </ResizableBox>
                  </Box>
                </TableCell> */}
                {/* Actions column with resizer */}
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem", p: 0, position: "relative", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} width={colWidths.id}>
                  <Box sx={{ display: "flex", alignItems: "center", height: 1 }}>
                    <Box sx={{ px: 1, py: 1, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Actions</Box>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {indexes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">No index tasks found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                indexes.map(index => (
                  <TableRow key={index.index_id} hover sx={{ fontSize: "0.95rem" }}>
                    {/* Name (fully shown) */}
                    <TableCell sx={{ py: 1, fontSize: "0.95rem", fontWeight: 500, maxWidth: colWidths.name, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} width={colWidths.name}>
                      <span>
                          <Link to={`/kb/${index.type}/${index.index_id}`} style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}>
                            {index.name}
                          </Link>
                        </span>
                    </TableCell>
                    {/* Index ID */}
                    {/* <TableCell sx={{ fontSize: "0.95rem", py: 1, maxWidth: colWidths.id, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} width={colWidths.id}>
                      <Tooltip title={index.index_id} arrow>
                        <span>
                          <Link to={`/kb/${index.type}/${index.index_id}`} style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}>
                            {index.index_id}
                          </Link>
                        </span>
                      </Tooltip>
                    </TableCell> */}
                    {/* Description (multi-line, no resizer) */}
                    <TableCell sx={{ py: 1, fontSize: "0.95rem", whiteSpace: "pre-line", maxWidth: colWidths.description, overflow: "hidden", textOverflow: "ellipsis" }} width={colWidths.description}>
                      {index.description || "--"}
                    </TableCell>
                    {/* Status (styled as before) */}
                    {/* <TableCell sx={{ py: 1, maxWidth: colWidths.status, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} width={colWidths.status}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 2,
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          color: index.status === "completed" ? "#fff" : "#333",
                          bgcolor: index.status === "completed"
                            ? "#43a047"
                            : index.status === "running"
                              ? "#ffb300"
                              : index.status === "processing"
                                ? "#1976d2"
                                : index.status === "failed"
                                  ? "#d32f2f"
                                  : "#e0e0e0",
                          boxShadow: index.status === "completed" ? 2 : 0,
                          display: "inline-block",
                          minWidth: 60,
                          textAlign: "center"
                        }}>
                          {index.status || "Unknown"}
                        </Box>
                      </Box>
                    </TableCell> */}
                    <TableCell sx={{ py: 1 }}>
                      <Chip
                        label={index.status || "Unknown"}
                        sx={{
                          bgcolor: index.status === "completed"
                            ? "#43a047"
                            : index.status === "running"
                              ? "#ffb300"
                              : index.status === "processing"
                                ? "#1976d2"
                                : index.status === "failed"
                                  ? "#d32f2f"
                                  : "#e0e0e0",
                          color: index.status === "completed" ? "#fff" : "#333",
                          fontWeight: 500,
                          borderRadius: "9999px", // fully rounded
                          height: 24,
                          px: 1.5,
                        }}
                    />
                </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Button
                        onClick={() => handleDelete(index.index_id, index.name)}
                        disabled={deleting === index.index_id}
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: "0.85rem", px: 1.5, py: 0.5 }}
                      >
                        {deleting === index.index_id ? "Deleting..." : "Delete"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}

export default function KnowledgeBuilderListPage() {
  return (
    <Box sx={{ pt:1, px: 3 }}>
       <Toolbar disableGutters sx={{ display: "flex", alignItems: "center"}}>
        <Typography variant="h4" sx={{ pl: 0 }}>Knowledge Builder Collections</Typography>
      </Toolbar>
      <Box sx={{ pt:1}}>
      <IndexStatusTable
        indexType="doc"
        title="Document Collections"
      />
      </Box>
      {/* <Divider sx={{ my: 4 }} />
      <IndexStatusTable
        indexType="code"
        title="Code RAG Indexes"
      /> */}
    </Box>
  );
}
